/**
 * Presentation vocabulary for per-server device health on the Live Servers
 * roster (feat-274). Built to `design/device-health.html` §6, which is the
 * prescriptive handoff — every class string below is reproduced from it
 * verbatim.
 *
 * Layering, same split `liveServersRoster.ts` already uses: the WIRE type and
 * its coercion live in `services/liveServersService.ts`; this module is FORMAT +
 * COLOR + ORDER only. **Nothing here classifies.** `state` arrives classified
 * server-side and is rendered as-is; the durations are explanation attached to a
 * state, never an input to choosing one. That is the rule
 * `admin/SquareConnectionCard.tsx` states in-file one domain over — "warning
 * styling is driven by connection state, NEVER elapsed time" — and the reason
 * this feature exists at all is a cell that reported health it had not measured.
 *
 * HARD CONSTRAINT, carried from `restaurant/StatusChip.tsx`: "Static literal
 * class strings per branch, never interpolated — Tailwind purges classes it
 * cannot see as whole tokens, and a purged class fails SILENTLY as an unstyled
 * render." Every map below is a `Record<state, string>` of COMPLETE strings. Do
 * not refactor one into a template that interpolates a tone.
 */
import type { DeviceHealth, DeviceHealthState, MatchState, RosterRow } from '../../services/liveServersService';

// ---------------------------------------------------------------------------
// Chip styling — whole literals, never interpolated
// ---------------------------------------------------------------------------

/** Pill geometry shared with feat-086's `StatusChip` so the two read as one family. */
export const HEALTH_CHIP_BASE =
  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap';

/**
 * Fill + ink per state.
 *
 * `stalled` is the ONE escalated variant in this palette — solid `bg-danger` with
 * white ink, 4.829:1. Every other chip in the app is a pale wash, and measured
 * (design/contrast.js, from the live tailwind.config.js) a pale wash cannot carry
 * an alarm: `danger.surface` is **1.094:1** against the white card it sits on and
 * **1.034:1** against the healthy chip beside it. Pale fills in this palette are
 * luminance-identical and separate only by hue, the first channel to go with
 * distance, glare and colour-vision deficiency. A pale red chip is a fine label
 * and a useless alarm, and the acceptance criterion here is an alarm.
 * **Do not harmonise `stalled` back to a wash.**
 */
export const HEALTH_CHIP: Record<DeviceHealthState, string> = {
  recording: 'bg-success-light text-success-on',
  stalled: 'bg-danger text-white',
  no_telemetry: 'bg-warning-surface text-attention-on',
  paused: 'bg-warning-surface text-attention-on',
  connecting: 'bg-warning-surface text-attention-on',
  offline: 'bg-cream-dark text-inkMuted',
  unknown: 'bg-white text-inkMuted ring-1 ring-inkMuted/40',
};

/**
 * Leading dot per state. `stalled` and `unknown` are empty because they lead with
 * an icon instead — `unknown` is deliberately a distinct SHAPE, not a colour
 * variant (feat-199: empty asserts a measurement, error asserts ignorance, and
 * the two must differ on more axes than hue).
 *
 * These dots sit below 3:1 and that is compliant only because they are
 * redundant: WCAG 1.4.11 exempts graphics that are not required to understand
 * the content, and the chip's TEXT LABEL carries the meaning. **The label is
 * therefore not optional** — drop it to save column width and four graphics
 * become meaning-bearing and non-compliant.
 */
export const HEALTH_DOT: Record<DeviceHealthState, string> = {
  recording: 'bg-success',
  stalled: '',
  no_telemetry: 'bg-warning-solid',
  paused: 'bg-warning-solid',
  connecting: 'bg-warning-solid animate-pulse',
  offline: 'bg-inkMuted',
  unknown: '',
};

/** On-screen copy renders through this map, never through literals in JSX. */
export const HEALTH_LABEL: Record<DeviceHealthState, string> = {
  recording: 'Recording',
  stalled: 'Mic silent',
  no_telemetry: 'No signal',
  paused: 'Paused',
  connecting: 'Connecting…',
  offline: 'Not in a shift',
  unknown: 'Health unknown',
};

/** Which states lead with an icon rather than a dot. Drives the JSX branch only. */
export type HealthGlyph = 'dot' | 'mic-off' | 'help';

export const HEALTH_GLYPH: Record<DeviceHealthState, HealthGlyph> = {
  recording: 'dot',
  stalled: 'mic-off',
  no_telemetry: 'dot',
  paused: 'dot',
  connecting: 'dot',
  offline: 'dot',
  unknown: 'help',
};

// ---------------------------------------------------------------------------
// Durations
// ---------------------------------------------------------------------------

/** Treat a zone-less backend string as UTC (mirrors `liveServersRoster.toUtcDate`). */
function toUtcMs(iso: string): number {
  const hasZone = iso.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(iso);
  return Date.parse(hasZone ? iso : `${iso}Z`);
}

/**
 * Elapsed time as a DURATION: `4s` · `6m` · `2h 14m` · `23h 31m` · `4d 7h`.
 *
 * Deliberately not `src/utils/liveServerStatus.ts`'s `formatElapsed()`, which
 * emits `h:mm:ss`. At this feature's magnitudes that renders `23:31:17`, which is
 * indistinguishable from a clock time in a table whose other columns ARE clock
 * times (`11:32am`).
 *
 * `nowMs` must be the SERVER frame (`as_of` + client elapsed), the same reference
 * `formatActivityAgo` already takes — a manager's laptop running eight minutes
 * fast must not be able to turn a healthy device amber.
 *
 * Returns `null` for a missing or unparseable timestamp so the caller can drop
 * the line entirely. It never substitutes another field for a missing one.
 */
export function formatDuration(iso: string | null | undefined, nowMs: number): string | null {
  if (!iso) return null;
  const t = toUtcMs(iso);
  if (Number.isNaN(t)) return null;

  return formatSeconds((nowMs - t) / 1000);
}

/**
 * The same duration vocabulary as `formatDuration`, fed from a number of seconds
 * the backend already measured (feat-342's `*_age_seconds`). Negative input clamps
 * to 0. Callers handle `null` themselves — this never invents a value.
 */
export function formatSeconds(seconds: number): string {
  const totalSec = Math.max(0, Math.floor(seconds));
  if (totalSec < 60) return `${totalSec}s`;

  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) return `${totalMin}m`;

  const totalHr = Math.floor(totalMin / 60);
  if (totalHr < 24) {
    const mins = totalMin - totalHr * 60;
    return mins === 0 ? `${totalHr}h` : `${totalHr}h ${mins}m`;
  }

  const days = Math.floor(totalHr / 24);
  const hrs = totalHr - days * 24;
  return hrs === 0 ? `${days}d` : `${days}d ${hrs}h`;
}

export interface HealthSubline {
  text: string;
  cls: string;
}

const SUBLINE_DANGER = 'text-[11px] font-bold text-danger-text whitespace-nowrap';
const SUBLINE_ATTENTION = 'text-[11px] font-bold text-attention-on whitespace-nowrap';
const SUBLINE_MUTED = 'text-[11px] text-inkMuted whitespace-nowrap';

/**
 * The line under the chip — the elapsed time that EXPLAINS the state the backend
 * already classified. `offline` and `unknown` carry no sub-line.
 *
 * Every branch renders only if the timestamp it names is present, and returns
 * `null` otherwise. **No branch falls back to another field, and in particular
 * none falls back to `last_activity_at`** — that measures speech, and per
 * DATA-REQUEST-3.md E2 only 51% of shifts stay under ten minutes of silence at
 * their quietest, so that fallback would caption every slow afternoon as a fault.
 *
 * (`stalled` reads `since` rather than `last_audio_at`, per design §6 verbatim —
 * for a broken row the quantity that matters is how long it has been broken.
 * Raised as QUESTIONS-FRONTEND.md Q1.)
 */
export function healthSubline(health: DeviceHealth, nowMs: number): HealthSubline | null {
  switch (health.state) {
    case 'stalled': {
      const d = formatDuration(health.since, nowMs);
      return d ? { text: `No audio for ${d}`, cls: SUBLINE_DANGER } : null;
    }
    case 'no_telemetry': {
      const d = formatDuration(health.last_telemetry_at, nowMs);
      return d ? { text: `Phone last seen ${d} ago`, cls: SUBLINE_ATTENTION } : null;
    }
    case 'paused': {
      const d = formatDuration(health.since, nowMs);
      return d ? { text: `Paused ${d} ago`, cls: SUBLINE_ATTENTION } : null;
    }
    case 'recording': {
      const d = formatDuration(health.last_audio_at, nowMs);
      return d ? { text: `Audio ${d} ago`, cls: SUBLINE_MUTED } : null;
    }
    case 'connecting':
      return { text: 'No audio yet', cls: SUBLINE_MUTED };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Severity ladder — ONE order across both axes
// ---------------------------------------------------------------------------

/**
 * feat-086 minted the left stripe as the MATCH-STATE band. feat-274 puts a second
 * axis on the same row, so the stripe's meaning is restated as **"the most urgent
 * thing about this row"** and one ordered ladder drives both the stripe colour and
 * the sort — which is what keeps the stripe column reading as clean bands
 * top-to-bottom.
 *
 * A dead microphone outranks every mapping problem: one is happening on the floor
 * right now, the other is back-office data hygiene.
 *
 * `connecting` is deliberately absent. A stripe pins a row to the top of the
 * sort, and a two-second transient must not.
 */
export const ROW_SEVERITY: Record<string, number> = {
  'health:stalled': 0,
  'health:no_telemetry': 1,
  'match:square_only_unmapped': 2,
  'match:square_only': 3,
  'health:paused': 4,
  'match:bespoke_only': 5,
};

/** Everything not on the ladder. Sorts last, renders no stripe. */
export const SEVERITY_UNRANKED = 99;

/**
 * Stripe per rank. 4px inset box-shadow so it does not shift cell padding.
 * A literal hex is required inside an arbitrary box-shadow value; these mirror
 * the bound tokens.
 *
 * Rank 1/4/5 use `attention.line #AE8213` (3.499:1) and **not** `warning.solid
 * #F59E0B`, which measures 2.148:1 on the card — under the 3:1 non-text floor.
 * `STRIPE_CLASSES` in LiveServers.tsx shipped that defect; this map replaces it
 * wholesale, so the defect goes with it. Do not port the old amber forward.
 */
export const SEVERITY_STRIPE: Record<number, string> = {
  0: 'shadow-[inset_4px_0_0_0_#DC2626]',
  1: 'shadow-[inset_4px_0_0_0_#AE8213]',
  2: 'shadow-[inset_4px_0_0_0_#DC2626]',
  3: 'shadow-[inset_4px_0_0_0_#DC2626]',
  4: 'shadow-[inset_4px_0_0_0_#AE8213]',
  5: 'shadow-[inset_4px_0_0_0_#AE8213]',
};

/** The state a row renders. A row with no `device_health` is `unknown`, never healthy. */
export function rowHealthState(row: RosterRow): DeviceHealthState {
  return row.device_health ? row.device_health.state : 'unknown';
}

/**
 * The row's rank on the ladder: the more urgent of its two axes. Rows off the
 * ladder on both axes get `SEVERITY_UNRANKED`.
 */
export function rowSeverityRank(row: RosterRow): number {
  const health = row.device_health ? ROW_SEVERITY[`health:${row.device_health.state}`] : undefined;
  const match = ROW_SEVERITY[`match:${row.match_state as MatchState}`];
  return Math.min(health ?? SEVERITY_UNRANKED, match ?? SEVERITY_UNRANKED);
}

/** Stripe class for a row, or `''` for a row with nothing urgent about it. */
export function rowStripeClass(row: RosterRow): string {
  return SEVERITY_STRIPE[rowSeverityRank(row)] ?? '';
}

// ---------------------------------------------------------------------------
// Legend + column copy — constants, never literals in JSX
// ---------------------------------------------------------------------------

export const MIC_COLUMN_HEADING = 'Mic';

/**
 * feat-274 AC3. `last_activity_at` is `MAX(transcript_part.created)` and measures
 * SPEECH. Leaving it generically labelled beside a new audio-recency value is
 * exactly how this bug gets rebuilt: a manager reads "Last activity 47m ago" as a
 * dead microphone and interrupts a server working a slow afternoon perfectly well.
 */
export const LAST_SPEECH_COLUMN_HEADING = 'Last speech';

/**
 * The legend keys the STRIPE, and the stripe now means severity across both axes
 * (see ROW_SEVERITY), so it can no longer be keyed to the four match states — a
 * `both` row with a dead microphone renders a red stripe beside a green "Both"
 * chip, and that is correct. Raised as QUESTIONS-FRONTEND.md Q3.
 */
export const SEVERITY_LEGEND_LEAD = 'Sorted most urgent first:';

export const SEVERITY_LEGEND: ReadonlyArray<{ className: string; label: string }> = [
  { className: 'bg-danger', label: 'Urgent — mic silent, or an unreconciled Square shift' },
  { className: 'bg-attention-line', label: 'Needs attention — no signal, paused, or Bespoke-only' },
];
